import React from 'react';
import {
  Box,
  costFormater,
  HintPlacement,
  InfoOutlineIcon,
  ShimmerBox,
  SimpleHint,
  Text,
} from '@common-fe/common-fe';

import { EMPTY_FIELD_VALUE } from '@/common/constants';

import useGetInvestmentsStatusQuery from './queries/useGetInvestmentStatus.query';
import { AutoInvestmentStatus } from './EnrollmentInvestments.types';
import EnrollmentInvestmentsItem from './EnrollmentInvestmentsItem';
import EnrollmentInvestmentsWrapper from './EnrollmentInvestmentsWrapper';
import { useGetInvestorFormattedData } from './hooks';

interface Props {
  employeeId: string;
  isAccountClosed?: boolean;
}

const EnrollmentDetailsInvestmentStatus: React.FC<Props> = ({ employeeId, isAccountClosed }) => {
  const { isLoading, data, isNotFound } = useGetInvestmentsStatusQuery(employeeId);

  const { investorStatus, investmentType, riskModel, isPendingStatus, isNotEnrolledStatus } =
    useGetInvestorFormattedData(data);
  if (isAccountClosed) {
    return (
      <EnrollmentInvestmentsWrapper>
        <Text size="large" weight={700}>
          Investments cannot be setup for closed accounts
        </Text>
      </EnrollmentInvestmentsWrapper>
    );
  }

  if (isNotFound || !data || isNotEnrolledStatus) {
    return (
      <EnrollmentInvestmentsWrapper>
        <EnrollmentInvestmentsItem testId="investments" title="Investments" color="textDisabled">
          Not Enrolled
        </EnrollmentInvestmentsItem>
        <Box style={{ flex: 1 }} />
      </EnrollmentInvestmentsWrapper>
    );
  }
  if (isLoading) {
    return (
      <EnrollmentInvestmentsWrapper>
        <Box width="25%" margin={{ right: 'spacing16' }}>
          <ShimmerBox />
        </Box>
        <Box width="45%">
          <ShimmerBox />
        </Box>
      </EnrollmentInvestmentsWrapper>
    );
  }
  return (
    <EnrollmentInvestmentsWrapper>
      <Box fill gap="spacing4">
        <EnrollmentInvestmentsItem
          testId="investments"
          isNodeMode
          title="Investments"
          color={investorStatus?.color}
        >
          <Box direction="row" align="center">
            <Text size="medium" data-testId="investments_value" color={investorStatus?.color} weight={400}>
              {investorStatus?.text}
            </Text>

            {isPendingStatus ? (
              <Box margin={{ left: 'spacing4' }}>
                <SimpleHint
                  style={{ lineHeight: 1 }}
                  preferAlign={HintPlacement.TOP}
                  width="180px"
                  content={
                    <Text size="small" weight={500} textAlign="center">
                      Enrollment into the investment account has not been completed.
                    </Text>
                  }
                >
                  <Box margin={{ top: '1px' }}>
                    <InfoOutlineIcon size="17px" color="iconSecondary" />
                  </Box>
                </SimpleHint>
              </Box>
            ) : null}
          </Box>
        </EnrollmentInvestmentsItem>
        <EnrollmentInvestmentsItem testId="investment-model" title="Investments model">
          {investmentType ? investmentType : EMPTY_FIELD_VALUE}
        </EnrollmentInvestmentsItem>
        <EnrollmentInvestmentsItem testId="risk-model" title="Risk model">
          {riskModel ? riskModel : EMPTY_FIELD_VALUE}
        </EnrollmentInvestmentsItem>
      </Box>
      <Box
        width={{ min: '1px' }}
        height={{ min: '100%' }}
        margin={{ horizontal: 'spacing24' }}
        background="border2"
      />
      <Box fill gap="spacing4">
        <EnrollmentInvestmentsItem testId="investments-balance" title="Investment balance">
          {costFormater(data?.investmentBalance || 0, { isPrecision: true })}
        </EnrollmentInvestmentsItem>
        <EnrollmentInvestmentsItem testId="plan-investment-threshold" title="Plan investment threshold">
          {costFormater(data?.planInvestmentThreshold || 0, { isPrecision: true })}
        </EnrollmentInvestmentsItem>
        {data?.autoInvestmentStatus ? (
          <>
            <EnrollmentInvestmentsItem
              testId="auto-investments"
              title="Automatic investments"
              color={data?.autoInvestmentStatus === AutoInvestmentStatus.ACTIVE ? 'success' : 'textBody'}
            >
              {data?.autoInvestmentStatus === AutoInvestmentStatus.ACTIVE ? 'Active' : 'Paused'}
            </EnrollmentInvestmentsItem>
            <Box height={{ min: 'spacing4' }} />
            <EnrollmentInvestmentsItem
              testId="automatic-investments-threshold"
              title="Automatic investments threshold"
            >
              {costFormater(data?.amountToKeep || 0, { isPrecision: true })}
            </EnrollmentInvestmentsItem>
          </>
        ) : (
          <EnrollmentInvestmentsItem
            testId="auto-investments"
            title="Automatic investments"
            color="textDisabled"
          >
            Not Set
          </EnrollmentInvestmentsItem>
        )}
      </Box>
    </EnrollmentInvestmentsWrapper>
  );
};
export default EnrollmentDetailsInvestmentStatus;
